import React, { useCallback, useEffect, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import {
    Box,
    Button,
    Dialog,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { useSnackbar } from 'notistack';
import { useContentApi, useDictionaryApi } from 'api/dictionary';
import {
    fetchRequestDocuments,
    OrderCommentDto,
    OrderDocument,
    OrderDto,
    OrderHistoryDto,
} from 'api/orders';
import Spin from 'components/atoms/Spin/Spin';
import ImageGallery from '../../molecules/ImageGallery/ImageGallery';
import CardInfoItem from '../../atoms/CardInfoItem/CardInfoItem';
import OrderEditDialog from '../OrderEditDialog';
import { useStyles } from './styles';
import { Props } from './props';
import { getNPODetails, NPODto } from '../../../api/npo';
import OrderBranchEditDialog from '../OrderBranchEditDialog/OrderBranchEditDialog';
import OrderAdressEditDialog from '../OrderAdressEditDialog/OrderAdressEditDialog';
import { OrderAddressTypeEnum } from '../OrderAdressEditDialog/OrderAddressEnum';
import { useSelector } from 'react-redux';
import { getUserInfo } from 'store/slices/userInfoSlice';
import { isDeliveryType } from 'pages/Orders/types';

const initOrderAddressEditOpen: {
    isEdit: boolean,
    addressType: OrderAddressTypeEnum
    isGeocoding?: boolean,
    isGeozoning?: boolean
} = { isEdit: false, addressType: OrderAddressTypeEnum.NONE, isGeocoding: false, isGeozoning: false };

const OrderDetailsDialog = ({ id, open, onClose, hasPermissionToEdit, onChange, isGeozoning }: Props) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [openEdit, setOpenEdit] = useState(false);
    const [isBranchEditOpen, setBranchEditOpen] = useState(false);
    const [addressEditOpen, setAddressEditOpen] = useState(initOrderAddressEditOpen);
    const [value, setValue] = React.useState(0);
    const [order, setOrder] = useState<OrderDto>();
    const [comments, setComments] = useState<OrderCommentDto[]>([]);
    const [histories, setHistories] = useState<OrderHistoryDto[]>([]);
    const [documents, setDocuments] = useState<OrderDocument[]>([]);
    const [npo, setNpo] = useState<NPODto[]>([]);

    const { permissions } = useSelector(getUserInfo);
    const hasPermissionToEditAddress = ['ADMIN', 'OPERATOR', 'SUPPORT', 'DISPATCHER'].includes(permissions[0]);

    //@ts-ignore
    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setValue(newValue);
    };

    const handleBranchEditOpen = useCallback(() => {
        setBranchEditOpen(!isBranchEditOpen);
    }, [isBranchEditOpen]);

    const handleTargetAddress = useCallback((addressType) => {
        setAddressEditOpen({
            ...addressEditOpen,
            isEdit: !addressEditOpen.isEdit,
            addressType,
        });
    }, [addressEditOpen.isEdit]);

    const handleOpenEdit = () => {
        setOpenEdit(true);
    };

    const { getById } = useDictionaryApi<OrderDto>('/order');
    const { fetchAll: getComments } = useContentApi<OrderCommentDto>(`/order-comment/${id}`);
    const { fetchAll: getHistories } = useContentApi<OrderHistoryDto>(
        `/order-status-history/${id}`
    );
    const load = useCallback(async () => {
        try {
            if (!id) {
                return;
            }

            setLoading(true);
            const res = await getById(id.toString());
            const commentList = await getComments();
            const historyList = await getHistories();
            const npoList = await getNPODetails(id);
            const documentList = await fetchRequestDocuments(id);
            setOrder(res);
            setComments(commentList);
            setHistories(historyList);
            setDocuments(documentList);
            // @ts-ignore
            if (npoList && npoList.npos !== undefined && Array.isArray(npoList.npos)) {
                // @ts-ignore
                setNpo(npoList.npos);
            }
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar, getById, getComments, getHistories, id]);

    const handleEditClose = () => {
        setOpenEdit(false);
        load();
    };

    const onBranchEditSuccess = useCallback(() => {   
        setBranchEditOpen(false);
        load();
    }, [load]);

    const onOrderAddressEditSuccess = useCallback(() => {
        setAddressEditOpen(initOrderAddressEditOpen);
        load();
    }, [load]);

    useEffect(() => {
        if (open) {
            setValue(0);
            load();
        }
    }, [getById, getComments, getHistories, load, open]);
    return (
        <>
            <Dialog
                classes={{ paper: classes.dialogPaper }}
                open={open}
                fullWidth={true}
                maxWidth="xl"
                onClose={onClose}
            >
                <DialogTitle>Детали заявки</DialogTitle>
                {(order?.product?.code === 'CC' || order?.product?.code === 'POSTKZ') && (
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.editButton}
                        onClick={handleOpenEdit}
                    >
                        Редактировать
                    </Button>
                )}
                <DialogContent>
                    <Paper className={classes.tabs}>
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            variant="standard"
                            indicatorColor="primary"
                            textColor="primary"
                        >
                            <Tab label="Основная информация" />
                            <Tab label="История заявки" />
                            <Tab label="История комментариев" />
                        </Tabs>
                        {loading && <Spin />}
                        {!loading && open && (
                            <Box className={classes.tabPane}>
                                <TabPanel value={value} index={0}>
                                    <Grid container spacing={3}>
                                        <Grid item xs>
                                            <CardInfoItem
                                                title="Номер заявки"
                                                body={order?.order_number}
                                            />
                                            <CardInfoItem
                                                title="Дата создание"
                                                body={order?.date_created}
                                            />
                                            <CardInfoItem
                                                title="Продукт"
                                                body={order?.product?.description}
                                            />
                                            <CardInfoItem
                                                title="Тип ЕМС"
                                                body={order?.mail_type?.description || '-'}
                                            />
                                            <CardInfoItem
                                                title="Возратное"
                                                body={order?.is_return ? 'Да' : 'Нет'}
                                            />
                                            <CardInfoItem
                                                title="Тип доставки"
                                                body={order?.order_detail?.delivery_type}
                                            />

                                            {order?.courier && (
                                                <CardInfoItem
                                                    title="Курьер"
                                                    body={[
                                                        `${order?.courier?.last_name} ${order?.courier?.first_name}`,
                                                        order?.courier?.phone_number,
                                                    ]}
                                                />
                                            )}
                                            {order?.date_delivered && (
                                                <CardInfoItem
                                                    title="Дата доставки"
                                                    body={order?.date_delivered}
                                                />
                                            )}
                                            <CardInfoItem
                                                title="Количество отправлений"
                                                body={order?.product.code !== "PPI" ? order?.order_detail?.item_count : order.newspapers[0].amount}
                                            />
                                            <CardInfoItem
                                                title="Количество фактических отправлений"
                                                body={order?.order_detail?.accepted_item_count}
                                            />
                                            {order?.related_orders !== undefined &&
                                                order?.related_orders?.length > 0 && (
                                                    <CardInfoItem
                                                        title="№ заказа"
                                                        body={
                                                            order?.related_orders[0].internet_number
                                                        }
                                                    />
                                                )}

                                            <CardInfoItem
                                                title="Статус"
                                                body={order?.order_status?.description}
                                            />
                                            <CardInfoItem
                                                title="Адрес отправки"
                                                styleBody={{background: order?.order_address?.source?.latitude || isDeliveryType(order?.order_detail?.delivery_type) || !isGeozoning ? 'white': 'red'}}
                                                body={`${order?.order_address?.source?.address} (${order?.order_address?.source?.latitude ? order?.order_address?.source?.latitude + ', ' + order?.order_address?.source?.longitude : '-'})`}
                                                isEditable={hasPermissionToEditAddress && order?.order_status?.code === 'GEOZONING'}
                                                onEditClick={() => handleTargetAddress(OrderAddressTypeEnum.SOURCE)}
                                            />
                                            <CardInfoItem
                                                title="Адрес доставки"
                                                styleBody={{background: order?.order_address?.target?.latitude || !isDeliveryType(order?.order_detail?.delivery_type) || !isGeozoning ? 'white': 'red'}}
                                                body={`${order?.order_address?.target?.address} (${order?.order_address?.target?.latitude ? order?.order_address?.target?.latitude + ', ' + order?.order_address?.target?.longitude : '-'})`}
                                                isEditable={hasPermissionToEditAddress && order?.order_status?.code === 'GEOZONING'}
                                                onEditClick={() => handleTargetAddress(OrderAddressTypeEnum.TARGET)}
                                            />
                                            <CardInfoItem
                                                title={'C денежной наличностью'}
                                                body={
                                                    order?.order_detail?.with_money ? 'Да' : 'Нет'
                                                }
                                            />
                                            <CardInfoItem
                                                title={'Наложенный платеж'}
                                                body={
                                                    order?.order_detail?.with_payment ? 'Да' : 'Нет'
                                                }
                                            />
                                            <CardInfoItem
                                                title="Сумма наложенного платежа"
                                                body={order?.order_detail?.payment_sum || '-'}
                                            />
                                            <CardInfoItem
                                                title="Комментарий отправителя"
                                                body={order?.order_address?.source?.order_comment}
                                            />
                                            <CardInfoItem
                                                title="Комментарий получателю"
                                                body={order?.order_address?.target?.order_comment}
                                            />
                                        </Grid>
                                        <Grid item xs>
                                            <CardInfoItem
                                                title="Регион"
                                                body={order?.region?.description}
                                            />
                                            <div>
                                                <CardInfoItem
                                                    title="Отделение"
                                                    body={order?.branch?.title}
                                                    isEditable={hasPermissionToEdit}
                                                    onEditClick={handleBranchEditOpen}
                                                />
                                            </div>
                                            <CardInfoItem
                                                title="Владелец"
                                                body={`${order?.tenant?.last_name} ${order?.tenant?.first_name}`}
                                            />
                                            <CardInfoItem
                                                title="Отправитель"
                                                body={order?.customers
                                                    ?.filter((c) => c.relation_type === 'SENDER')
                                                    .map(
                                                        (client) =>
                                                            `${[
                                                                client.customer?.name,
                                                                client.customer?.surname,
                                                                client.customer?.patronymic,
                                                            ]
                                                                .filter((c) => c)
                                                                .join(' ')}, ${[
                                                                    client.customer?.phone_number,
                                                                    client.customer?.email,
                                                                ]
                                                                    .filter((c) => c)
                                                                    .join(', ')}`
                                                    )}
                                            />
                                            <CardInfoItem
                                                title="Получатель"
                                                body={order?.customers
                                                    ?.filter((c) => c.relation_type === 'RECEIVER')
                                                    .map(
                                                        (client) =>
                                                            `${[
                                                                client.customer?.name,
                                                                client.customer?.surname,
                                                                client.customer?.patronymic,
                                                            ]
                                                                .filter((c) => c)
                                                                .join(' ')}, ${[
                                                                    client.customer?.phone_number,
                                                                    client.customer?.email,
                                                                ]
                                                                    .filter((c) => c)
                                                                    .join(', ')}`
                                                    )}
                                            />

                                            <CardInfoItem
                                                title="Тариф за доставку"
                                                body={order?.order_detail?.delivery_sum || '-'}
                                            />
                                            <CardInfoItem
                                                title="Вес товара"
                                                body={order?.order_detail?.weight || '-'}
                                            />
                                            <CardInfoItem
                                                title="Оплата получателем"
                                                body={
                                                    order?.order_detail?.is_payment_for_delivery
                                                        ? 'Да'
                                                        : 'Нет'
                                                }
                                            />
                                            <CardInfoItem
                                                title="Сумма за доставку"
                                                body={order?.order_detail?.client_amount || '-'}
                                            />

                                            {
                                                order?.product.code === "PPI" &&
                                                (<CardInfoItem
                                                    title="Наименование издания"
                                                    body={order.newspapers[0].name || '-'}
                                                />)
                                            }

                                            {order?.acceptance_orders &&
                                                order?.acceptance_orders?.length > 0 && (
                                                    <CardInfoItem
                                                        title="Принятые отправления"
                                                        body={order?.acceptance_orders
                                                            .map(
                                                                (orderItem) =>
                                                                    orderItem.order_number
                                                            )
                                                            .join(', ')}
                                                    />
                                                )}
                                            {npo.length > 0 &&
                                                npo.map((item) => {
                                                    const fact_count =
                                                        typeof item?.fact_count === 'number'
                                                            ? item.fact_count
                                                            : '-';
                                                    const count =
                                                        typeof item?.count === 'number'
                                                            ? item.count
                                                            : '-';
                                                    return (
                                                        <CardInfoItem
                                                            key={`${item.id}`}
                                                            title={`${item.description}(факт/общ)`}
                                                            body={`${fact_count}/${count}`}
                                                        />
                                                    );
                                                })}
                                        </Grid>
                                    </Grid>
                                    {documents.map((doc, index) => {
                                        const photos = doc.event_task_details.map((d) => ({
                                            id: d.asset.id,
                                            desc: d.date_created.toString(),
                                        }));

                                        return (
                                            <Box marginTop={2} key={index}>
                                                <Typography variant="h4">
                                                    {doc.description}
                                                </Typography>
                                                <Box marginTop={3} margin={3}>
                                                    <ImageGallery photos={photos} />
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </TabPanel>
                                <TabPanel value={value} index={1}>
                                    <Box>
                                        {loading && <Spin />}
                                        <TableContainer>
                                            <Table size={'small'} aria-label="simple table">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Статус</TableCell>
                                                        <TableCell>Дата и время</TableCell>
                                                        <TableCell>Пользователь</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {histories.map((item, index) => (
                                                        <TableRow key={index} hover={true}>
                                                            <TableCell>
                                                                {item.order_status?.description}
                                                            </TableCell>
                                                            <TableCell>
                                                                {item.date_created}
                                                            </TableCell>
                                                            <TableCell>{`${item.user?.last_name} ${item.user?.first_name}`}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                </TabPanel>
                                <TabPanel value={value} index={2}>
                                    <Box position={'relative'}>
                                        {loading && <Spin />}
                                        <TableContainer>
                                            <Table size={'small'} aria-label="simple table">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Комментарий</TableCell>
                                                        <TableCell>Дата и время</TableCell>
                                                        <TableCell>Пользователь</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {comments.map((item, index) => (
                                                        <TableRow key={index} hover={true}>
                                                            <TableCell>{item.body}</TableCell>
                                                            <TableCell>
                                                                {item.date_created}
                                                            </TableCell>
                                                            <TableCell>{`${item.user?.last_name} ${item.user?.first_name}`}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                </TabPanel>
                            </Box>
                        )}
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Отмена</Button>
                </DialogActions>
            </Dialog>
            {openEdit && (
                <OrderEditDialog
                    open={openEdit}
                    onClose={handleEditClose}
                    order={order || ({} as OrderDto)}
                />
            )}
            {hasPermissionToEdit && (
                <OrderBranchEditDialog
                    onClose={handleBranchEditOpen}
                    open={isBranchEditOpen}
                    order={order || ({} as OrderDto)}
                    onSuccess={onBranchEditSuccess}
                />
            )}
            {hasPermissionToEditAddress && (
                <OrderAdressEditDialog
                    onClose={() => handleTargetAddress(OrderAddressTypeEnum.NONE)}
                    onChange={onChange}
                    addressType={addressEditOpen.addressType}
                    open={addressEditOpen.isEdit}
                    order={order || ({} as OrderDto)}
                    onSuccess={onOrderAddressEditSuccess}
                />
            )}
        </>
    );
};

interface TabPanelProps {
    children?: React.ReactNode;
    index: any;
    value: any;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box p={3}>{children}</Box>}
        </div>
    );
}

export default OrderDetailsDialog;
